import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { repository } from '@/repositories';
import type { CompleteProfileInput, CreateInviteInput, CreateDeptInput, CreateUnitInput } from '@/repositories/types';
import type { Course, Lesson, LikertValue, ProfileAdminEdit, ProfileSelfEdit, QuizAnswers, Role, TestKind, UserStatus } from '@/types/domain';

/** Centralised query keys — keep cache invalidation honest. */
export const qk = {
  courses: ['courses'] as const,
  course: (id: string) => ['courses', id] as const,
  me: ['me'] as const,
  enrollments: ['enrollments'] as const,
  certificates: ['certificates'] as const,
  roster: (courseId?: string) => ['roster', courseId ?? 'all'] as const,
  questions: (bankId?: string) => ['questions', bankId ?? 'all'] as const,
  invites: ['invites'] as const,
  users: ['users'] as const,
  org: ['org'] as const,
  roleDefs: ['roleDefs'] as const,
};

/* ── Catalog ── */
export const useCourses = () => useQuery({ queryKey: qk.courses, queryFn: () => repository.listCourses() });
export const useCourse = (id: string) =>
  useQuery({ queryKey: qk.course(id), queryFn: () => repository.getCourse(id), enabled: !!id });

export function useUpdateCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Course> }) => repository.updateCourse(id, patch),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: qk.course(id) });
      qc.invalidateQueries({ queryKey: qk.courses });
    },
  });
}

export function useUpdateLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ courseId, lessonId, patch }: { courseId: string; lessonId: string; patch: Partial<Lesson> }) =>
      repository.updateLesson(courseId, lessonId, patch),
    onSuccess: (_d, { courseId }) => {
      qc.invalidateQueries({ queryKey: qk.course(courseId) });
      qc.invalidateQueries({ queryKey: qk.courses });
    },
  });
}

/* ── Learner ── */
export const useMe = () => useQuery({ queryKey: qk.me, queryFn: () => repository.getMe() });

export function useCompleteProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CompleteProfileInput) => repository.completeProfile(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.me }),
  });
}
export const useEnrollments = () =>
  useQuery({ queryKey: qk.enrollments, queryFn: () => repository.listMyEnrollments() });

export function useEnroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (courseId: string) => repository.enroll(courseId),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.enrollments }),
  });
}

export function useSubmitEvaluation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ courseId, answers, comment }: { courseId: string; answers: Record<string, LikertValue>; comment?: string }) =>
      repository.submitEvaluation(courseId, answers, comment),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.enrollments });
      qc.invalidateQueries({ queryKey: qk.certificates });
    },
  });
}

/* ── Certificates ── */
export const useCertificates = () =>
  useQuery({ queryKey: qk.certificates, queryFn: () => repository.listCertificates() });

export function useVerifyCertificate() {
  return useMutation({ mutationFn: (code: string) => repository.verifyCertificate(code) });
}

export function useIssueCertificate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (courseId: string) => repository.issueCertificate(courseId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.certificates });
      qc.invalidateQueries({ queryKey: qk.enrollments });
    },
  });
}

/* ── Instructor ── */
export const useRoster = (courseId?: string, refetchMs?: number) =>
  useQuery({ queryKey: qk.roster(courseId), queryFn: () => repository.listRoster(courseId), refetchInterval: refetchMs });
export const useQuestions = (bankId?: string) =>
  useQuery({ queryKey: qk.questions(bankId), queryFn: () => repository.listQuestions(bankId) });

export function useMarkAttendance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ courseId, userId }: { courseId: string; userId?: string }) => repository.markAttendance(courseId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roster'] });
      qc.invalidateQueries({ queryKey: qk.enrollments });
      qc.invalidateQueries({ queryKey: qk.certificates });
    },
  });
}

/** Instructor/helper opens or closes the check-in window (returns the venue token). */
export function useSetCheckin() {
  return useMutation({
    mutationFn: ({ courseId, open }: { courseId: string; open: boolean }) => repository.setCheckin(courseId, open),
  });
}

/** Learner scans the venue QR → marks self present. */
export function useCheckIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ courseId, token }: { courseId: string; token: string }) => repository.checkIn(courseId, token),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roster'] });
      qc.invalidateQueries({ queryKey: qk.enrollments });
      qc.invalidateQueries({ queryKey: qk.certificates });
    },
  });
}

/** Instructor/helper marks a specific roster learner present (scan-failed fallback). */
export function useMarkAttended() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ courseId, userId }: { courseId: string; userId: string }) => repository.markAttended(courseId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roster'] });
      qc.invalidateQueries({ queryKey: qk.enrollments });
    },
  });
}

/** Instructor/helper sets a learner present or absent (de-check-in with confirm). */
export function useSetAttended() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ courseId, userId, present }: { courseId: string; userId: string; present: boolean }) => repository.setAttended(courseId, userId, present),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roster'] });
      qc.invalidateQueries({ queryKey: qk.enrollments });
    },
  });
}

/* ── Admin ── */
export const useInvites = () =>
  useQuery({ queryKey: qk.invites, queryFn: () => repository.listInvites() });
export const useUsers = () => useQuery({ queryKey: qk.users, queryFn: () => repository.listUsers() });
export const useOrg = () => useQuery({ queryKey: qk.org, queryFn: () => repository.listOrg() });
export const useRoleDefs = () =>
  useQuery({ queryKey: qk.roleDefs, queryFn: () => repository.getRoleDefs() });

export function useCreateInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInviteInput) => repository.createInvite(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.invites }),
  });
}

export function useRevokeInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => repository.revokeInvite(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.invites }),
  });
}

export function useUpdateUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role }: { id: string; role: Role }) => repository.updateUserRole(id, role),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.users }),
  });
}

export function useSetUserStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: UserStatus }) =>
      repository.setUserStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.users }),
  });
}

export function useUpdateMyProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: ProfileSelfEdit) => repository.updateMyProfile(patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.me });
      qc.invalidateQueries({ queryKey: qk.users });
    },
  });
}

export function useAdminUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ProfileAdminEdit }) => repository.adminUpdateProfile(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.users }),
  });
}

/* ── Organization editor ── */
export function useCreateDept() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDeptInput) => repository.createDept(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.org }),
  });
}
export function useUpdateDept() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { nameEn?: string; nameTh?: string } }) => repository.updateDept(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.org }),
  });
}
export function useDeleteDept() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => repository.deleteDept(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.org }),
  });
}
export function useCreateUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUnitInput) => repository.createUnit(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.org }),
  });
}
export function useUpdateUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => repository.updateUnit(id, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.org }),
  });
}
export function useDeleteUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => repository.deleteUnit(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.org }),
  });
}

/* ── Assessment ── */
export const useTestQuestions = (courseId: string, kind?: TestKind) =>
  useQuery({ queryKey: ['test-questions', courseId, kind ?? 'all'], queryFn: () => repository.getTestQuestions(courseId, kind), enabled: !!courseId });

/** Learner review of a completed test — reveal-on-pass; the RPC throws `not_unlocked` until earned. */
export const useTestReview = (courseId: string | undefined, kind: 'pre' | 'post') =>
  useQuery({ queryKey: ['test-review', courseId, kind], queryFn: () => repository.getTestReview(courseId!, kind), enabled: !!courseId, retry: false });

/** Instructor survey (evaluation) summary for a course. */
export const useSurveySummary = (courseId: string | undefined) =>
  useQuery({ queryKey: ['survey-summary', courseId], queryFn: () => repository.getSurveySummary(courseId!), enabled: !!courseId });

/** Per-question item analysis (most-often-wrong) for a test. */
export const useItemAnalysis = (courseId: string | undefined, kind: 'pre' | 'post', refetchMs?: number) =>
  useQuery({ queryKey: ['item-analysis', courseId, kind], queryFn: () => repository.getItemAnalysis(courseId!, kind), enabled: !!courseId, refetchInterval: refetchMs });

export function useSubmitTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ courseId, kind, answers }: { courseId: string; kind: TestKind; answers: QuizAnswers }) =>
      repository.submitTest(courseId, kind, answers),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.enrollments });
      qc.invalidateQueries({ queryKey: qk.certificates });
    },
  });
}

export const useLessonQuiz = (lessonId: string) =>
  useQuery({ queryKey: ['lesson-quiz', lessonId], queryFn: () => repository.getLessonQuiz(lessonId), enabled: !!lessonId });

export function useSubmitLessonQuiz() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ lessonId, answers }: { lessonId: string; answers: QuizAnswers }) =>
      repository.submitLessonQuiz(lessonId, answers),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.courses });
      qc.invalidateQueries({ queryKey: qk.enrollments });
      qc.invalidateQueries({ queryKey: qk.certificates });
    },
  });
}

export function useCompleteLesson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lessonId: string) => repository.completeLesson(lessonId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.courses }); // prefix-matches course(id) too
      qc.invalidateQueries({ queryKey: qk.enrollments });
      qc.invalidateQueries({ queryKey: qk.certificates });
    },
  });
}
